import React, { useContext, useRef } from "react";
import { ResizeEnable, Rnd } from "react-rnd";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { CanvasContext, ICanvasComponent } from "../canvasContext.tsx";
import { resizeHandleClasses } from "../canvasUtils.tsx";
import ImageElement from "./ImageElement.tsx";
import TextElement from "./TextElement.tsx";
import VideoElement from "./VideoElement.tsx";
import SlideElement from "./SlideElement.tsx";
import Model3DElement from "./Model3DElement.tsx";

const DRAG_HANDLE_CLASS = "canvas-drag-handle";

const componentMap: { [key: string]: React.ComponentType<ICanvasComponent> } = {
  TEXT: TextElement,
  IMAGE: ImageElement,
  VIDEO: VideoElement,
  SLIDE: SlideElement,
  "3D_MODEL": Model3DElement,
};

const getEnableResize = (type: string): ResizeEnable => {
  const allowAll = ["IMAGE", "VIDEO", "SLIDE", "3D_MODEL"].includes(type);
  return {
    bottom: allowAll,
    bottomLeft: true,
    bottomRight: true,
    top: allowAll,
    topLeft: true,
    topRight: true,
    left: true,
    right: true,
  };
};
const CanvasComponent = (props: ICanvasComponent) => {
  const { state, actions } = useContext(CanvasContext);
  const { dimension, position, content, id, type, slideZoom, cameraOrbit } = props;
  const [showGrids, setShowGrids] = React.useState(false);
  const [isReadOnly, setIsReadOnly] = React.useState(true);
  const elementRef = React.useRef<HTMLDivElement>(null);
  const isDragged = useRef<boolean>(false);

  const activeSelection = state?.activeSelection;
  const canvasData = state?.canvasData ?? [];
  const layerIndex = id ? canvasData.findIndex((c) => c.id === id) : -1;
  const canMoveUp = layerIndex >= 0 && layerIndex < canvasData.length - 1;
  const canMoveDown = layerIndex > 0;

  const onBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const toolbarElement = document.querySelector("#toolbar");
    if (
      event.currentTarget.contains(event?.relatedTarget as Element) ||
      toolbarElement?.contains(event?.relatedTarget as Element)
    ) {
      return;
    }
    setIsReadOnly(true);
    actions?.setEnableQuillToolbar(false);
    if (id && activeSelection) {
      activeSelection.delete(id);
      actions?.setActiveSelection(new Set(activeSelection));
    }
    actions?.saveNow?.();
  };

  const getComponent = () => {
    const Component = type && componentMap[type];
    if (!Component || !id) return null;
    return (
      <Component
        key={id}
        id={id}
        type={type}
        position={position}
        dimension={dimension}
        content={content}
        isReadOnly={isReadOnly}
        slideZoom={slideZoom}
        cameraOrbit={cameraOrbit}
      />
    );
  };

  const style: React.CSSProperties = {
    outline: "none",
    border: `2px solid ${
      (id && state?.activeSelection.has(id)) || showGrids || isDragged.current
        ? "#06b6d4"
        : "transparent"
    }`
  };

  const onMouseEnter = () => {
    setShowGrids(true);
  };

  const onMouseLeave = () => {
    setShowGrids(false);
  };

  const onfocus = (event: React.MouseEvent) => {
    if (id) {
      actions?.setActiveSelection(new Set(state?.activeSelection.add(id)));
    }
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!isReadOnly) event.stopPropagation();
  };

  const handleClass =
    id && state?.activeSelection.has(id) && state?.activeSelection.size === 1
      ? "showHandles"
      : "";

  const onDoubleClick = () => {
    if (!isReadOnly) return;
    setIsReadOnly(false);
    actions?.setEnableQuillToolbar(true);
  };

  return (
    <div ref={elementRef}>
      <Rnd
        style={style}
        size={{ width: dimension?.width || 0, height: dimension?.height || 0 }}
        position={{ x: position?.left || 0, y: position?.top || 0 }}
        onDragStart={() => {
          isDragged.current = true;
        }}
        onDragStop={(e, d) => {
          isDragged.current = false;
          actions?.updateCanvasData({ id, position: { left: d.x, top: d.y } });
          actions?.saveNow?.();
        }}
        resizeHandleWrapperClass={handleClass}
        resizeHandleClasses={resizeHandleClasses}
        onResize={(e, direction, ref, delta, position) => {
          actions?.updateCanvasData({
            id,
            dimension: { width: ref.style.width, height: ref.style.height },
            position: { top: position.y, left: position.x }
          });
        }}
        onResizeStop={() => {
          actions?.saveNow?.();
        }}
        enableResizing={getEnableResize(type)}
        minWidth={100}
        minHeight={50}
        disableDragging={false}
        dragHandleClassName={DRAG_HANDLE_CLASS}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onDoubleClick={onDoubleClick}
        onKeyDown={onKeyDown}
        onFocus={onfocus}
        onBlur={onBlur}
        tabIndex={0}
        lockAspectRatio={false}
      >
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
          <div
            style={{
              position: "absolute",
              top: 4,
              right: 4,
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              gap: 4,
              pointerEvents: "auto",
            }}
          >
            <div
              className={DRAG_HANDLE_CLASS}
              style={{
                cursor: "move",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: 4,
                background: "rgba(0,0,0,0.65)",
                color: "#fff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
              }}
              title="Drag to move"
            >
              <OpenWithIcon sx={{ fontSize: 18 }} />
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                if (id && canMoveUp) actions?.moveElementLayer?.(id, "up");
              }}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && id && canMoveUp) {
                  e.preventDefault();
                  actions?.moveElementLayer?.(id, "up");
                }
              }}
              style={{
                cursor: canMoveUp ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: 4,
                background: "rgba(0,0,0,0.65)",
                color: canMoveUp ? "#fff" : "rgba(255,255,255,0.4)",
                boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
              }}
              title="Bring layer up"
            >
              <ArrowUpwardIcon sx={{ fontSize: 18 }} />
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                if (id && canMoveDown) actions?.moveElementLayer?.(id, "down");
              }}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && id && canMoveDown) {
                  e.preventDefault();
                  actions?.moveElementLayer?.(id, "down");
                }
              }}
              style={{
                cursor: canMoveDown ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: 4,
                background: "rgba(0,0,0,0.65)",
                color: canMoveDown ? "#fff" : "rgba(255,255,255,0.4)",
                boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
              }}
              title="Bring layer down"
            >
              <ArrowDownwardIcon sx={{ fontSize: 18 }} />
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                if (id) actions?.deleteElementById(id);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if (id) actions?.deleteElementById(id);
                }
              }}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: 4,
                background: "rgba(0,0,0,0.65)",
                color: "#fff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
              }}
              title="Delete"
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </div>
          </div>
          <div
            className="item-container"
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              zIndex: 0,
            }}
          >
            {getComponent()}
          </div>
        </div>
      </Rnd>
    </div>
  );
};

export default CanvasComponent;
